import { useState, useEffect, useMemo, useCallback } from 'react';
import { detectKeypoints } from '../lib/poseDetector';
import { computePhotoCanonical, canAutoMatch } from '../lib/photoAligner';
import { IDENTITY, autoMatch as solveAutoMatch } from '../lib/photoManualTransform';

// All state for ONE before/after pair. Everything in here used to live inline
// in PhotoComparisonViewer as duplicated before*/after* variables; extracting
// it is what lets the viewer render two pairs without doubling its state.
export default function usePairAlignment({ pair, onKeypointsComputed }) {
  const beforeUrl = pair?.before?.photoUrl || null;
  const afterUrl = pair?.after?.photoUrl || null;
  const beforePose = pair?.poseType?.before || '';
  const afterPose = pair?.poseType?.after || '';
  const propBeforeKp = pair?.before?.keypoints || null;
  const propAfterKp = pair?.after?.keypoints || null;

  const [b, setB] = useState(propBeforeKp);
  const [a, setA] = useState(propAfterKp);
  const [manualBefore, setManualBefore] = useState(IDENTITY);
  const [manualAfter, setManualAfter] = useState(IDENTITY);
  const [computing, setComputing] = useState(false);

  // Sync keypoint state with props when the photos change, then lazy-detect
  // anything still missing. Without the resync, picking a different date left
  // the alignment math running on the previous photo's keypoints.
  useEffect(() => {
    let cancelled = false;
    setB(propBeforeKp);
    setA(propAfterKp);
    async function run() {
      if (propBeforeKp && propAfterKp) return;
      setComputing(true);
      const tasks = [];
      if (!propBeforeKp && beforeUrl) {
        tasks.push(detectKeypoints(beforeUrl, beforePose).then(r => ({ side: 'b', r })));
      }
      if (!propAfterKp && afterUrl) {
        tasks.push(detectKeypoints(afterUrl, afterPose).then(r => ({ side: 'a', r })));
      }
      const results = await Promise.all(tasks);
      if (cancelled) return;
      for (const { side, r } of results) {
        const setter = side === 'b' ? setB : setA;
        setter(r.keypoints ? r : null);
        if (r.keypoints && onKeypointsComputed) {
          onKeypointsComputed(
            side === 'b' ? pair?.before?.checkInId : pair?.after?.checkInId,
            side === 'b' ? beforePose : afterPose,
            r,
          );
        }
      }
      setComputing(false);
    }
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beforeUrl, afterUrl, beforePose, afterPose, propBeforeKp, propAfterKp]);

  // A zoom/pan from one comparison must not carry into the next.
  useEffect(() => {
    setManualBefore(IDENTITY);
    setManualAfter(IDENTITY);
  }, [beforeUrl, afterUrl, beforePose, afterPose]);

  const beforeCanonical = useMemo(() => computePhotoCanonical(b?.keypoints), [b]);
  const afterCanonical = useMemo(() => computePhotoCanonical(a?.keypoints), [a]);

  const alignState = useMemo(() => {
    if (!b?.keypoints || !a?.keypoints) return { tone: 'grey', label: 'Manual only' };
    if (b.quality === 'warn' || a.quality === 'warn') return { tone: 'amber', label: 'Partial' };
    return { tone: 'green', label: 'Auto-aligned' };
  }, [b, a]);

  const reset = useCallback(() => {
    setManualBefore(IDENTITY);
    setManualAfter(IDENTITY);
  }, []);

  // Auto-match is only meaningful when both sides resolve to the same
  // reference landmarks — see canAutoMatch in lib/photoAligner.
  const matchable = !!b?.keypoints && !!a?.keypoints && canAutoMatch(beforePose, afterPose);

  const before = {
    ...(pair?.before || {}),
    url: beforeUrl,
    poseType: beforePose,
    keypoints: b?.keypoints || null,
    canonical: beforeCanonical,
    manual: manualBefore,
    setManual: setManualBefore,
    autoMatch: matchable
      ? () => setManualBefore(solveAutoMatch(b.keypoints, a.keypoints, beforePose, beforeCanonical, afterCanonical, afterPose) || IDENTITY)
      : null,
  };

  const after = {
    ...(pair?.after || {}),
    url: afterUrl,
    poseType: afterPose,
    keypoints: a?.keypoints || null,
    canonical: afterCanonical,
    manual: manualAfter,
    setManual: setManualAfter,
    autoMatch: matchable
      ? () => setManualAfter(solveAutoMatch(a.keypoints, b.keypoints, afterPose, afterCanonical, beforeCanonical, beforePose) || IDENTITY)
      : null,
  };

  return { before, after, alignState, computing, reset };
}
